import React, { useEffect, useState } from "react";
import { Check, KeyRound, Loader2, Landmark, Wallet as WalletIcon, Apple, Copy, Store, Unlock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, local } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const SERVICE_NAMES = {
  "notebook-lm": "دفتر AQL", builder: "استوديو البناء", page: "مولد الصفحات",
  publisher: "الناشر التلقائي", contracts: "صياغة العقود", notebook: "نوت بوك ذكي",
};

export default function Marketplace() {
  const [packages, setPackages] = useState([]);
  const [pay, setPay] = useState(null);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState("bank");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(local.getEmail());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [redeem, setRedeem] = useState("");

  useEffect(() => {
    api.packages().then((d) => setPackages(d.packages || []));
    api.paymentInfo().then(setPay);
  }, []);

  const openBuy = (pkg) => { setSelected(pkg); setResult(null); setMethod("bank"); setOpen(true); };

  const confirm = async () => {
    if (!name.trim() || !email.trim()) { toast.error("أدخل الاسم والبريد الإلكتروني"); return; }
    if (method === "apple") { toast.info("الدفع عبر Apple Pay يتطلب ربط بوابة Stripe (بمفتاحك)."); return; }
    setLoading(true);
    try {
      local.setEmail(email);
      const order = await api.createOrder({
        kind: "package", package_id: selected.id, amount: selected.price,
        method, email, name,
      });
      if (order.status === "paid" && order.key_code) {
        const info = await api.redeemKey({ code: order.key_code, email });
        local.setServiceKey(info.code);
        local.setOwnerAccess(!!info.master);
        local.addUnlocked(info.services || []);
        setResult({ paid: true, key: order.key_code, services: info.services });
        toast.success("تم الدفع وتفعيل الباقة!");
      } else {
        setResult({ paid: false, reference: order.reference });
        toast.success("تم إنشاء الطلب — حوّل المبلغ ثم انتظر اعتماد الإدارة");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر إتمام الطلب");
    }
    setLoading(false);
  };

  const doRedeem = async () => {
    if (!redeem.trim()) return;
    try {
      const info = await api.redeemKey({ code: redeem.trim(), email });
      local.setServiceKey(info.code);
      local.setOwnerAccess(!!info.master);
      local.addUnlocked(info.services || []);
      toast.success(`تم تفعيل: ${(info.services || []).map((s) => SERVICE_NAMES[s]).join("، ")}`);
      setRedeem("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "مفتاح غير صحيح");
    }
  };

  return (
    <main className="relative min-h-screen pt-32">
      <div className="absolute left-1/2 top-24 h-64 w-[700px] -translate-x-1/2 hero-glow blur-3xl" />
      <section className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-amber-400/5 px-4 py-1.5 text-xs font-medium text-gold">
            <Store className="h-3.5 w-3.5" /> المتجر · باقات المفاتيح
          </span>
          <h1 className="mt-4 font-display text-5xl font-extrabold text-white">اختر باقتك</h1>
          <p className="mt-3 text-muted-foreground">رسوم رمزية لتشغيل الخدمات الذكية فقط — بلا أرباح تجارية.</p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {packages.map((p) => (
            <div key={p.id} className={`card-hover relative flex flex-col rounded-3xl border p-7 ${p.highlight ? "border-cyan/40 bg-gradient-to-b from-cyan-500/10 to-transparent" : "border-white/10 bg-white/[0.02]"}`}>
              {p.highlight && <span className="absolute -top-3 right-1/2 translate-x-1/2 rounded-full btn-cyan px-3 py-1 text-xs font-bold">{p.tag}</span>}
              <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1.5">
                <span className="font-display text-4xl font-extrabold text-white">{p.price}</span>
                <span className="mb-1 text-sm text-muted-foreground">ريal</span>
              </div>
              {!p.highlight && <p className="mt-1 text-xs text-muted-foreground">{p.tag}</p>}
              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" strokeWidth={3} /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex flex-wrap gap-1.5">
                {p.services.map((s) => <span key={s} className="rounded-md bg-white/5 px-2 py-0.5 text-[11px] text-cyan">{SERVICE_NAMES[s]}</span>)}
              </div>
              <Button onClick={() => openBuy(p)} className={`mt-6 h-11 w-full rounded-xl font-semibold ${p.highlight ? "btn-cyan hover:bg-transparent" : "border border-white/15 bg-white/5 text-white hover:bg-white/10"}`}>
                شراء الباقة
              </Button>
            </div>
          ))}
        </div>

        {/* redeem */}
        <div className="mx-auto mt-14 max-w-xl rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <div className="flex items-center gap-2 text-white"><Unlock className="h-5 w-5 text-cyan" /><h3 className="font-display font-bold">لديك مفتاح تفعيل؟</h3></div>
          <p className="mt-1 text-sm text-muted-foreground">أدخل مفتاحك لفتح الخدمات الذكية فورًا.</p>
          <div className="mt-4 flex gap-2">
            <Input value={redeem} onChange={(e) => setRedeem(e.target.value)} placeholder="AQL4-XXXX-XXXX-XXXX" className="border-white/10 bg-white/5 font-en text-white" dir="ltr" />
            <Button onClick={doRedeem} className="btn-gold gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><KeyRound className="h-4 w-4" /> تفعيل</Button>
          </div>
        </div>
      </section>
      <div className="h-20" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#0b1116] sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right font-display text-white">شراء {selected?.name}</DialogTitle>
            <DialogDescription className="text-right text-muted-foreground">المبلغ: {selected?.price} ريال</DialogDescription>
          </DialogHeader>

          {!result && (
            <div className="space-y-4 py-1">
              <div className="grid gap-3">
                <div><Label className="text-xs text-white">الاسم</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 border-white/10 bg-white/5 text-right text-white" /></div>
                <div><Label className="text-xs text-white">البريد الإلكتروني</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border-white/10 bg-white/5 text-white" dir="ltr" /></div>
              </div>
              <div>
                <Label className="mb-2 block text-xs text-white">طريقة الدفع</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "bank", label: "تحويل بنكي", Icon: Landmark },
                    { id: "wallet", label: "المحفظة", Icon: WalletIcon },
                    { id: "apple", label: "Apple Pay", Icon: Apple },
                  ].map(({ id, label, Icon }) => (
                    <button key={id} onClick={() => setMethod(id)}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-xs font-medium transition-colors ${method === id ? "border-cyan/50 bg-cyan/10 text-cyan" : "border-white/10 text-muted-foreground hover:text-white"}`}>
                      <Icon className="h-5 w-5" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {method === "bank" && pay && (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm">
                  <p className="text-muted-foreground">حوّل المبلغ إلى:</p>
                  <div className="mt-2 space-y-1 text-white">
                    <div>البنك: <span className="text-gold">{pay.bank_name}</span></div>
                    <div>المستفيد: {pay.bank_beneficiary}</div>
                    <div className="flex items-center gap-2">IBAN: <span className="font-en text-cyan" dir="ltr">{pay.bank_iban}</span></div>
                  </div>
                </div>
              )}
              {method === "apple" && <p className="rounded-lg bg-amber-400/5 p-3 text-xs text-amber-200/90">Apple Pay يتطلب ربط بوابة Stripe بمفتاحك — جاهز للتفعيل فور توفير المفتاح.</p>}
              {method === "wallet" && <p className="rounded-lg bg-cyan/5 p-3 text-xs text-cyan">سيُخصم المبلغ من رصيد محفظتك ويُصدر المفتاح فورًا.</p>}

              <Button onClick={confirm} disabled={loading} className="btn-cyan h-11 w-full gap-2 rounded-xl font-semibold hover:bg-transparent">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} تأكيد الطلب
              </Button>
            </div>
          )}

          {result && (
            <div className="py-3 text-center">
              {result.paid ? (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl btn-cyan"><Check className="h-7 w-7" strokeWidth={3} /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">تم التفعيل!</h3>
                  <p className="mt-1 text-sm text-muted-foreground">مفتاحك:</p>
                  <div className="mt-2 flex items-center justify-center gap-2">
                    <code className="rounded-lg bg-white/5 px-3 py-1.5 font-en text-cyan" dir="ltr">{result.key}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(result.key); toast.success("تم النسخ"); }}><Copy className="h-4 w-4 text-muted-foreground hover:text-white" /></button>
                  </div>
                  <p className="mt-3 text-sm text-cyan">تم فتح: {(result.services || []).map((s) => SERVICE_NAMES[s]).join("، ")}</p>
                </>
              ) : (
                <>
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/10 text-gold"><Landmark className="h-7 w-7" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-white">بانتظار اعتماد الإدارة</h3>
                  <p className="mt-2 text-sm text-muted-foreground">تم تسجيل طلبك برقم مرجعي:</p>
                  <code className="mt-1 inline-block rounded-lg bg-white/5 px-3 py-1.5 font-en text-cyan" dir="ltr">{result.reference}</code>
                  <p className="mt-3 text-xs text-muted-foreground">حوّل المبلغ ثم أرسل الإيصال عبر واتساب. سيصدر مفتاحك بعد الاعتماد.</p>
                </>
              )}
              <Button onClick={() => setOpen(false)} variant="outline" className="mt-5 h-10 w-full rounded-xl border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">إغلاق</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
